/**
 * SabbaticalPanel — admin UI for member participation breaks.
 *
 * Opened from Admin → Members (🏖️ button on a member card).
 *
 * Flow is deliberately two-step and mirrors the backend's dry-run contract:
 *
 *    1. PREVIEW  POST /api/sabbaticals            (no apply flag → writes nothing)
 *       Shows exactly which months clear, which fee rows would be removed, and
 *       which are held back because money sits on them.
 *    2. CONFIRM  POST /api/sabbaticals?apply=true
 *
 * There is no way to reach step 2 without seeing step 1. That is the point —
 * backdating deletes fee rows, and SB should never be one click from that.
 *
 * The whole-month rule (a month clears only if the break covers all of it) is
 * enforced server-side in services/sabbatical.js. This file never computes it.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtDate = d => {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth() + 1]} ${dt.getUTCFullYear()}`;
};
const rupees = n => '₹' + Number(n || 0).toLocaleString('en-IN');
const today = () => new Date().toISOString().slice(0, 10);

const S = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: '64px',
    background: 'rgba(0,0,0,0.6)', zIndex: 300,
    display: 'flex', alignItems: 'flex-end',
  },
  sheet: {
    background: 'white', borderRadius: '20px 20px 0 0', width: '100%',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
  },
  grab: { width: 40, height: 4, background: '#e5e7eb', borderRadius: 2, margin: '0 auto 16px' },
  body: { overflowY: 'auto', padding: '20px 20px 0', flex: 1 },
  title: { fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 },
  sub: { fontSize: '0.78rem', color: '#6b7280', marginBottom: 16 },
  foot: {
    padding: '12px 20px 16px',
    borderTop: '1px solid #f3f4f6', background: 'white',
    display: 'flex', gap: 8, flexShrink: 0,
  },
  btn: (bg, color) => ({
    flex: 1, padding: '12px', borderRadius: 10, border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: '0.9rem', background: bg, color,
  }),
  note: (bg, border, color) => ({
    background: bg, border: `1px solid ${border}`, borderRadius: 8,
    padding: 10, marginBottom: 12, fontSize: '0.78rem', color, lineHeight: 1.5,
  }),
  pill: {
    display: 'inline-block', padding: '3px 9px', borderRadius: 999,
    background: '#eef2ff', color: '#3730a3', fontSize: '0.72rem',
    fontWeight: 700, marginRight: 6, marginBottom: 6,
  },
  row: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 10px', borderRadius: 8, marginBottom: 6, fontSize: '0.8rem',
  },
};

export default function SabbaticalPanel({ member, onClose, onSaved }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const [endingId, setEndingId] = useState(null);
  const [returnDate, setReturnDate] = useState(today());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/sabbaticals', { params: { member_id: member.id } });
      setHistory(r.data.sabbaticals || []);
    } catch (e) {
      setErr(e?.response?.data?.error || 'Failed to load sabbatical history');
    } finally {
      setLoading(false);
    }
  }, [member.id]);

  useEffect(() => { load(); }, [load]);

  // "Currently away" means a period covering TODAY — not merely one with no end
  // date. A sabbatical recorded as 1 Jun → 31 Dec is still current in August.
  const open = history.find(s => {
    const t = today();
    const st = String(s.start_date).slice(0, 10);
    const en = s.end_date ? String(s.end_date).slice(0, 10) : null;
    return st <= t && (!en || en >= t);
  });

  const runPreview = async () => {
    setErr(''); setPreview(null);
    if (!startDate) { setErr('Pick a start date.'); return; }
    if (endDate && endDate < startDate) { setErr('End date is before the start date.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/sabbaticals', {
        member_id: member.id,
        start_date: startDate,
        end_date: endDate || null,
        reason: reason.trim() || null,
      });
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.error || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const applyIt = async () => {
    setErr(''); setBusy(true);
    try {
      const r = await api.post('/sabbaticals?apply=true', {
        member_id: member.id,
        start_date: startDate,
        end_date: endDate || null,
        reason: reason.trim() || null,
      });
      setDone(r.data);
      setPreview(null);
      await load();
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const endSabbatical = async id => {
    setErr(''); setBusy(true);
    try {
      const r = await api.patch(`/sabbaticals/${id}/end`, { end_date: returnDate });
      setDone({ note: r.data.note, months_needing_fees: r.data.months_needing_fees });
      setEndingId(null);
      await load();
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not end sabbatical');
    } finally {
      setBusy(false);
    }
  };

  const removeSabbatical = async id => {
    if (!window.confirm(
      'Delete this sabbatical record?\n\nFee rows it removed are NOT restored — you will need to regenerate those months.'
    )) return;
    setBusy(true);
    try {
      const r = await api.delete(`/sabbaticals/${id}`);
      setDone({ note: r.data.warning, months_needing_fees: r.data.affected_months });
      await load();
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not delete');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setPreview(null); setDone(null); setErr(''); };

  return (
    <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.sheet}>
        <div style={S.body}>
          <div style={S.grab} />
          <div style={S.title}>🏖️ Sabbatical</div>
          <div style={S.sub}>{member.display_name} · {member.ucc_id}</div>

          {err && <div style={S.note('#fff1f2', '#fca5a5', '#991b1b')}>{err}</div>}

          {done && (
            <div style={S.note('#f0fdf4', '#bbf7d0', '#166534')}>
              <strong>Saved.</strong> {done.note}
              {done.months_needing_fees?.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  Run <strong>Generate Fees</strong> for: {done.months_needing_fees.join(', ')}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
          ) : (
            <>
              {/* ── Currently away ── */}
              {open && !preview && (
                <div style={{ ...S.note('#fffbeb', '#fde68a', '#92400e'), marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    On sabbatical since {fmtDate(open.start_date)}
                    {open.end_date ? ` until ${fmtDate(open.end_date)}` : ''}
                  </div>
                  {open.reason && <div style={{ marginBottom: 8 }}>{open.reason}</div>}
                  <div>No fees are generated for any month fully covered by this break.</div>

                  {endingId === open.id ? (
                    <div style={{ marginTop: 10 }}>
                      <label className="form-label">Date he returned</label>
                      <input type="date" className="form-control"
                        value={returnDate} onChange={e => setReturnDate(e.target.value)} />
                      <div style={{ fontSize: '0.72rem', margin: '6px 0 8px' }}>
                        The month he returns in is charged in full.
                        {open.end_date && ' This replaces the end date already recorded.'}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-outline" style={{ flex: 1 }}
                          onClick={() => setEndingId(null)}>Cancel</button>
                        <button style={S.btn('#16a34a', 'white')} disabled={busy}
                          onClick={() => endSabbatical(open.id)}>
                          {busy ? 'Saving…' : 'Confirm return'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button style={{ ...S.btn('#16a34a', 'white'), marginTop: 10, width: '100%' }}
                      onClick={() => { setEndingId(open.id); setReturnDate(today()); }}>
                      ✅ {open.end_date ? 'Change return date' : 'Mark returned'}
                    </button>
                  )}
                </div>
              )}

              {/* ── Start a new one ── */}
              {!open && !preview && !done && (
                <>
                  <div style={S.note('#eff6ff', '#bfdbfe', '#1e40af')}>
                    A sabbatical pauses fees and availability without making him an
                    ex-member. Dates may be in the past. A month is only exempt if the
                    break covers <strong>the whole month</strong> — so a break starting
                    12 March still charges March in full.
                  </div>

                  <div className="form-group">
                    <label className="form-label">Sabbatical started *</label>
                    <input type="date" className="form-control"
                      value={startDate} onChange={e => setStartDate(e.target.value)} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Returned on (leave blank if still away)</label>
                    <input type="date" className="form-control"
                      value={endDate} onChange={e => setEndDate(e.target.value)} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Reason</label>
                    <input type="text" className="form-control"
                      placeholder="e.g. Work posting out of Ludhiana"
                      value={reason} onChange={e => setReason(e.target.value)} />
                  </div>
                </>
              )}

              {/* ── Dry-run result ── */}
              {preview && (
                <>
                  <div style={S.note('#fffbeb', '#fde68a', '#92400e')}>
                    <strong>Nothing has been saved yet.</strong> This is what will happen.
                  </div>

                  <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6 }}>
                    Months that become fee-free
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    {preview.exempt_months?.length
                      ? preview.exempt_months.map(m => <span key={m} style={S.pill}>{m}</span>)
                      : <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          None — the break doesn't cover any full month.
                        </span>}
                  </div>

                  {preview.fees_removed?.length > 0 && (
                    <>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6, color: '#dc2626' }}>
                        Fee rows that will be removed ({preview.fees_removed.length})
                      </div>
                      {preview.fees_removed.map(f => (
                        <div key={f.fee_id} style={{ ...S.row, background: '#fff1f2' }}>
                          <span style={{ fontWeight: 600 }}>{f.label}</span>
                          <span>{rupees(f.amount_due)} · unpaid</span>
                        </div>
                      ))}
                      <div style={{ fontSize: '0.72rem', color: '#6b7280', margin: '4px 0 14px' }}>
                        Removed rows are recoverable — they're soft-deleted, not erased.
                      </div>
                    </>
                  )}

                  {preview.fees_kept?.length > 0 && (
                    <>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6, color: '#d97706' }}>
                        Left alone — money against them ({preview.fees_kept.length})
                      </div>
                      {preview.fees_kept.map(f => (
                        <div key={f.fee_id} style={{ ...S.row, background: '#fffbeb', display: 'block' }}>
                          <div style={{ fontWeight: 600 }}>{f.label}</div>
                          <div style={{ fontSize: '0.74rem', color: '#92400e' }}>{f.reason}</div>
                        </div>
                      ))}
                      <div style={{ fontSize: '0.72rem', color: '#92400e', margin: '4px 0 14px' }}>
                        These are never deleted automatically — removing them would leave
                        payment records pointing at a fee that no longer exists. Decide
                        each one yourself.
                      </div>
                    </>
                  )}

                  {!preview.fees_removed?.length && !preview.fees_kept?.length && (
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 14 }}>
                      No existing fee rows in those months — nothing to clean up.
                    </div>
                  )}
                </>
              )}

              {/* ── History ── */}
              {history.length > 0 && !preview && (
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6 }}>
                    History
                  </div>
                  {history.map(s => (
                    <div key={s.id} style={{ ...S.row, background: '#f9fafb', display: 'block' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600 }}>
                          {fmtDate(s.start_date)} → {s.end_date ? fmtDate(s.end_date) : 'ongoing'}
                        </span>
                        <button onClick={() => removeSabbatical(s.id)} disabled={busy}
                          style={{ border: '1px solid #fca5a5', background: '#fff1f2', color: '#dc2626',
                            borderRadius: 6, padding: '3px 8px', fontSize: '0.68rem',
                            fontWeight: 700, cursor: 'pointer' }}>
                          Delete
                        </button>
                      </div>
                      {s.reason && (
                        <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>{s.reason}</div>
                      )}
                      {s.created_by_name && (
                        <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 2 }}>
                          Recorded by {s.created_by_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <div style={{ height: 8 }} />
        </div>

        {/* ── Footer ── */}
        <div style={S.foot}>
          {preview ? (
            <>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={reset}>Back</button>
              <button style={S.btn('#1E3A6E', 'white')} disabled={busy} onClick={applyIt}>
                {busy ? 'Saving…' : `Confirm${preview.totals?.removed ? ` · remove ${preview.totals.removed}` : ''}`}
              </button>
            </>
          ) : open || done ? (
            <button style={S.btn('#1E3A6E', 'white')} onClick={onClose}>Close</button>
          ) : (
            <>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
              <button style={S.btn('#1E3A6E', 'white')} disabled={busy || !startDate} onClick={runPreview}>
                {busy ? 'Checking…' : 'Preview changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
